/**
 * Conformité des clients (FNE) — tableau regroupé par société.
 * Une ligne par société (NCC / RCCM / adresse consolidés), dépliable pour voir
 * les contacts et leurs téléphones/emails. Règles de validation identiques au
 * preflight FNE (services/fne_conformity.py).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, ArrowUpDown, CheckCircle2, ChevronDown, ChevronRight, Clock, Download,
  ExternalLink, Eye, FileWarning, Loader2, MailCheck, Minus, Pencil, RefreshCw, RotateCcw,
  Search, Send, Users,
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { ClientConformityPanel, FIELD_DEFS } from '../../components/admin/ClientConformityPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOT = { ok: 'text-emerald-700', blocking: 'text-rose-600', missing: 'text-amber-600', na: 'text-slate-300' };

const FNE_BADGE = {
  compliant: { label: 'Conforme', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  incomplete: { label: 'À compléter', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  blocked: { label: 'Bloqué', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  excluded: { label: 'Hors FNE', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const COMPANY_COLS = [
  { key: 'ncc', label: 'NCC' },
  { key: 'rccm', label: 'RCCM' },
  { key: 'address', label: 'Adresse' },
];

const StatusDot = ({ status }) => {
  const Icon = status === 'ok' ? CheckCircle2 : status === 'blocking' ? AlertTriangle
    : status === 'missing' ? Clock : Minus;
  return <Icon className={`h-3.5 w-3.5 shrink-0 ${DOT[status] || DOT.na}`} />;
};

const FieldCell = ({ field, editing, editValue, setEditValue, onStart, onSave, onCancel, fieldKey, error }) => {
  if (editing) {
    return (
      <td className="px-2 py-1">
        <input autoFocus value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }}
          onBlur={onSave}
          placeholder={FIELD_DEFS[fieldKey]?.placeholder}
          className={`w-full h-7 px-1.5 border rounded text-xs ${error ? 'border-rose-400' : 'border-blue-400'}`}
          data-testid={`cell-input-${fieldKey}`} />
        {error && <div className="text-[9px] text-rose-600">{error}</div>}
      </td>
    );
  }
  return (
    <td onClick={() => field.status !== 'na' && onStart()}
      className={`px-3 py-1.5 ${field.status !== 'na' ? 'cursor-text hover:bg-blue-50' : ''}`}
      title={field.status === 'na' ? 'Non applicable' : 'Cliquer pour modifier'}>
      <div className="flex items-center gap-1.5">
        <StatusDot status={field.status} />
        <span className={`truncate max-w-[130px] ${field.status === 'ok' ? 'text-slate-700' : 'text-slate-400 italic'}`}>
          {field.status === 'ok' ? (field.value || '—')
            : field.status === 'na' ? '—'
            : field.kind === 'invalid' ? (field.value || 'Non conforme') : 'Manquant'}
        </span>
        {field.kind === 'invalid' && field.value && (
          <span className="text-[9px] text-rose-600 font-semibold shrink-0">non conforme</span>
        )}
      </div>
    </td>
  );
};

const KpiCard = ({ label, value, sub, icon: Icon, color, pct, testid }) => {
  const tone = {
    slate: 'bg-white border-slate-200 text-slate-900', emerald: 'bg-emerald-50/60 border-emerald-200 text-emerald-900',
    amber: 'bg-amber-50/60 border-amber-200 text-amber-900', rose: 'bg-rose-50/60 border-rose-200 text-rose-900',
  }[color];
  const bar = { slate: 'bg-slate-400', emerald: 'bg-emerald-500', amber: 'bg-amber-500', rose: 'bg-rose-500' }[color];
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${tone}`} data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 opacity-70" />
        <div className="text-xl font-bold tabular-nums">{value}</div>
      </div>
      <div className="text-[11px] font-medium opacity-80 mt-0.5">{label}</div>
      {sub && <div className="text-[10px] opacity-60">{sub}</div>}
      {pct != null && (
        <div className="mt-1.5 h-1 rounded-full bg-black/5 overflow-hidden">
          <div className={`h-full ${bar}`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      )}
    </div>
  );
};

export default function ClientCompletenessPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alerting, setAlerting] = useState(false);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [commercialFilter, setCommercialFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [anomalyFilter, setAnomalyFilter] = useState('all');
  const [showTests, setShowTests] = useState(false);
  const [sort, setSort] = useState({ key: 'anomalies', dir: 'desc' });
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});
  const [panelId, setPanelId] = useState(null);
  const [editing, setEditing] = useState(null);   // {clientId, field}
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState(false);

  const fetchTable = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/clients/completeness/table`);
      setData(d);
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec du chargement', variant: 'destructive' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTable(); }, [fetchTable]);

  const sendAlerts = async (dry = false) => {
    if (dry) setDryRunLoading(true); else setAlerting(true);
    try {
      const { data: res } = await axios.post(`${API}/clients/completeness/alert`, { dry_run: dry });
      const notified = res.results?.length || 0;
      const sent = res.results?.filter(r => r.email_sent).length || 0;
      toast({
        title: dry ? 'Simulation terminée' : 'Relances envoyées',
        description: dry ? `${notified} commercial(iaux) aurai(ent) reçu une relance`
          : `${sent} email(s) envoyé(s) · ${notified} commercial(iaux) notifié(s)`,
      });
      if (!dry) fetchTable();
    } catch (e) {
      toast({ title: 'Erreur envoi', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { if (dry) setDryRunLoading(false); else setAlerting(false); }
  };

  const summary = (showTests ? data?.summaryAll : data?.summary) || {};

  const rows = useMemo(() => {
    let out = (data?.items || []).filter(g => showTests || !g.isTest);
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter(g => [g.companyName, ...g.commercials, g.company.ncc.value,
        ...g.contacts.flatMap(c => [c.contactName, c.fields.email.value, c.fields.phone.value])]
        .some(v => String(v || '').toLowerCase().includes(q)));
    }
    if (commercialFilter !== 'all') {
      out = out.filter(g => g.contacts.some(c => (c.commercialId || 'none') === commercialFilter));
    }
    if (statusFilter !== 'all') out = out.filter(g => g.fneStatus === statusFilter);
    if (anomalyFilter !== 'all') {
      out = out.filter(g => (['ncc', 'rccm', 'address'].includes(anomalyFilter)
        ? ['blocking', 'missing'].includes(g.company[anomalyFilter]?.status)
        : g.contacts.some(c => ['blocking', 'missing'].includes(c.fields[anomalyFilter]?.status))));
    }
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...out].sort((a, b) => (sort.key === 'companyName'
      ? a.companyName.localeCompare(b.companyName) * dir
      : ((a.anomalies - b.anomalies) * dir || a.companyName.localeCompare(b.companyName))));
  }, [data, search, commercialFilter, statusFilter, anomalyFilter, sort, showTests]);

  useEffect(() => { setPage(1); }, [search, commercialFilter, statusFilter, anomalyFilter, pageSize, showTests]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);
  const allContacts = rows.flatMap(g => g.contacts);
  const panelIndex = allContacts.findIndex(c => c.clientId === panelId);
  const panelRow = panelIndex >= 0
    ? { ...allContacts[panelIndex], companyName: allContacts[panelIndex].companyName || allContacts[panelIndex].contactName }
    : null;

  const toggleSort = (key) => setSort(s => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));
  const resetFilters = () => {
    setSearch(''); setCommercialFilter('all'); setStatusFilter('all'); setAnomalyFilter('all');
  };

  const cellError = () => {
    if (!editing) return null;
    const v = String(editValue || '').trim();
    if (!v) return null;
    return FIELD_DEFS[editing.field]?.check(v) || null;
  };

  const saveCell = async () => {
    if (!editing || savingCell) return;
    const def = FIELD_DEFS[editing.field];
    const value = String(editValue || '').trim();
    if (!value || value === String(editing.initial ?? '').trim() || cellError()) { setEditing(null); return; }
    setSavingCell(true);
    try {
      const { data: res } = await axios.patch(
        `${API}/clients/completeness/clients/${editing.clientId}`, { [def.payloadKey]: value });
      toast({
        title: `${def.label} enregistré`,
        description: res.propagatedTo
          ? `Appliqué aussi aux ${res.propagatedTo} autre(s) compte(s) de ${res.companyName}.`
          : (res.isCompliant ? 'Fiche conforme FNE.' : undefined),
      });
      setEditing(null);
      fetchTable();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast({ title: 'Valeur refusée',
        description: detail?.errors?.[0]?.message || (typeof detail === 'string' ? detail : undefined),
        variant: 'destructive' });
      setEditing(null);
    } finally { setSavingCell(false); }
  };

  const startEdit = (clientId, field, initial) => {
    setEditing({ clientId, field, initial: initial ?? '' });
    setEditValue(initial ?? '');
  };

  const openPanelForGroup = (g) => {
    const target = g.contacts.find(c => c.anomalies > 0) || g.contacts[0];
    setPanelId(target.clientId);
  };

  const exportCsv = () => {
    const head = ['Société', 'Contact', 'Commercial', 'Téléphone', 'Email', 'NCC', 'RCCM', 'Adresse', 'Statut FNE', 'Anomalies'];
    const body = rows.flatMap(g => g.contacts.map(c => [
      g.companyName, c.contactName, c.commercialName, c.fields.phone.value || '', c.fields.email.value || '',
      g.company.ncc.value || '', g.company.rccm.value || '', g.company.address.value || '',
      FNE_BADGE[c.fneStatus]?.label || c.fneStatus, c.anomalies,
    ]));
    const csv = [head, ...body].map(l => l.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `conformite-clients-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="completeness-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Conformité des clients</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Une ligne par société — dépliez pour corriger le téléphone et l’email de chaque contact
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={exportCsv} disabled={!rows.length}
            className="inline-flex items-center gap-1.5 text-sm border border-slate-200 bg-white rounded-lg px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
            data-testid="completeness-export"><Download className="h-3.5 w-3.5" /> Exporter</button>
          <button onClick={fetchTable} disabled={loading}
            className="inline-flex items-center gap-1.5 text-sm border border-slate-200 bg-white rounded-lg px-3 py-1.5 hover:bg-slate-50"
            data-testid="completeness-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Actualiser</button>
          <button onClick={() => sendAlerts(true)} disabled={dryRunLoading || alerting}
            className="inline-flex items-center gap-1.5 text-sm border border-blue-200 text-blue-700 bg-blue-50 rounded-lg px-3 py-1.5 hover:bg-blue-100"
            data-testid="completeness-simulate">
            {dryRunLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MailCheck className="h-3.5 w-3.5" />} Simuler</button>
          <button onClick={() => sendAlerts(false)} disabled={alerting || dryRunLoading}
            className="inline-flex items-center gap-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1.5"
            data-testid="completeness-send">
            {alerting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Envoyer les relances</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Comptes clients" value={summary.totalClients || 0}
          sub={`${summary.totalCompanies || 0} sociétés`} icon={Users} color="slate" testid="kpi-total" />
        <KpiCard label={`Conformes · ${summary.compliancePct || 0} %`} value={summary.compliant || 0}
          icon={CheckCircle2} color="emerald" pct={summary.compliancePct || 0} testid="kpi-compliant" />
        <KpiCard label="À compléter" value={summary.toComplete || 0} icon={Clock} color="amber"
          pct={summary.totalClients ? (summary.toComplete / summary.totalClients) * 100 : 0} testid="kpi-tocomplete" />
        <KpiCard label="Bloqués FNE" value={summary.blocked || 0} icon={FileWarning} color="rose"
          pct={summary.totalClients ? (summary.blocked / summary.totalClients) * 100 : 0} testid="kpi-blocked" />
      </div>

      <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-xl p-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-3.5 w-3.5 absolute left-3 top-2.5 text-slate-400 pointer-events-none" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une société, un contact, un numéro…"
            className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-sm"
            data-testid="completeness-search" />
        </div>
        <select value={commercialFilter} onChange={(e) => setCommercialFilter(e.target.value)}
          className="h-8 border border-slate-200 rounded-lg text-sm px-2 bg-white" data-testid="filter-commercial">
          <option value="all">Tous les commerciaux</option>
          {(data?.commercials || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          <option value="none">Non assigné</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="h-8 border border-slate-200 rounded-lg text-sm px-2 bg-white" data-testid="filter-status">
          <option value="all">Tous les statuts</option>
          <option value="compliant">Conformes</option>
          <option value="blocked">Bloqués FNE</option>
          <option value="incomplete">Informations incomplètes</option>
          <option value="excluded">Hors périmètre FNE</option>
        </select>
        <select value={anomalyFilter} onChange={(e) => setAnomalyFilter(e.target.value)}
          className="h-8 border border-slate-200 rounded-lg text-sm px-2 bg-white" data-testid="filter-anomaly">
          <option value="all">Toutes les anomalies</option>
          <option value="phone">Téléphone</option>
          <option value="email">Email</option>
          <option value="ncc">NCC</option>
          <option value="rccm">RCCM</option>
          <option value="address">Adresse</option>
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer px-2">
          <input type="checkbox" checked={showTests} onChange={(e) => setShowTests(e.target.checked)}
            className="h-3.5 w-3.5 accent-slate-600" data-testid="filter-tests" />
          Inclure les comptes de test
        </label>
        <button onClick={resetFilters}
          className="inline-flex items-center gap-1.5 text-sm border border-slate-200 rounded-lg px-2.5 h-8 hover:bg-slate-50"
          data-testid="filter-reset"><RotateCcw className="h-3.5 w-3.5" /> Réinitialiser</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[62vh] overflow-y-auto">
          <table className="w-full text-xs" data-testid="conformity-table">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2.5 text-left">
                  <button onClick={() => toggleSort('companyName')} className="inline-flex items-center gap-1 hover:text-slate-800"
                    data-testid="sort-client">Société / Contact <ArrowUpDown className="h-3 w-3" /></button>
                </th>
                <th className="px-3 py-2.5 text-left">Commercial</th>
                <th className="px-3 py-2.5 text-left">Téléphone</th>
                {COMPANY_COLS.map(c => <th key={c.key} className="px-3 py-2.5 text-left">{c.label}</th>)}
                <th className="px-3 py-2.5 text-left">Statut FNE</th>
                <th className="px-3 py-2.5 text-center">
                  <button onClick={() => toggleSort('anomalies')} className="inline-flex items-center gap-1 hover:text-slate-800"
                    data-testid="sort-anomalies">Anomalies <ArrowUpDown className="h-3 w-3" /></button>
                </th>
                <th className="px-3 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={9} className="py-10 text-center text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto" /></td></tr>
              )}
              {!loading && pageRows.length === 0 && (
                <tr data-testid="conformity-empty"><td colSpan={9} className="py-10 text-center text-slate-400">
                  Aucun client ne correspond aux filtres.</td></tr>
              )}
              {!loading && pageRows.map(g => {
                const isOpen = !!expanded[g.groupKey];
                const multi = g.contactsCount > 1;
                const first = g.contacts[0];
                return (
                  <React.Fragment key={g.groupKey}>
                    <tr className="hover:bg-blue-50/40" data-testid={`conformity-row-${g.groupKey}`}>
                      <td className="px-3 py-1.5 font-medium">
                        <div className="flex items-center gap-1">
                          {multi ? (
                            <button onClick={() => setExpanded(e => ({ ...e, [g.groupKey]: !isOpen }))}
                              className="p-0.5 rounded hover:bg-slate-200 text-slate-500"
                              data-testid={`expand-${g.groupKey}`}>
                              {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            </button>
                          ) : <span className="w-[18px] inline-block" />}
                          <Link to={`/admin/clients?tab=companies&highlight=${first.clientId}`}
                            className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900">
                            <span className="truncate max-w-[170px]">{g.companyName}</span>
                            <ExternalLink className="h-3 w-3 shrink-0" />
                          </Link>
                          {multi && (
                            <span className="text-[10px] text-slate-400 shrink-0">· {g.contactsCount} contacts</span>
                          )}
                          {g.isTest && (
                            <span className="text-[9px] bg-slate-100 text-slate-500 px-1 rounded shrink-0">test</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-slate-600 truncate max-w-[130px]">{g.commercials.join(', ')}</td>
                      {multi ? (
                        <td className="px-3 py-1.5 text-slate-500">
                          <div className="flex items-center gap-1.5">
                            <StatusDot status={g.phoneSummary.issues ? 'blocking' : 'ok'} />
                            {g.phoneSummary.issues
                              ? `${g.phoneSummary.issues} contact(s) à corriger`
                              : `${g.phoneSummary.ok} contact(s) conformes`}
                          </div>
                        </td>
                      ) : (
                        <FieldCell
                          field={first.fields.phone} fieldKey="phone"
                          editing={editing?.clientId === first.clientId && editing?.field === 'phone'}
                          editValue={editValue} setEditValue={setEditValue} error={cellError()}
                          onStart={() => startEdit(first.clientId, 'phone', first.fields.phone.value)}
                          onSave={saveCell} onCancel={() => setEditing(null)} />
                      )}
                      {COMPANY_COLS.map(col => (
                        <FieldCell key={col.key}
                          field={g.company[col.key]} fieldKey={col.key}
                          editing={editing?.clientId === first.clientId && editing?.field === col.key}
                          editValue={editValue} setEditValue={setEditValue} error={cellError()}
                          onStart={() => startEdit(first.clientId, col.key, g.company[col.key].value)}
                          onSave={saveCell} onCancel={() => setEditing(null)} />
                      ))}
                      <td className="px-3 py-1.5">
                        <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full border ${FNE_BADGE[g.fneStatus]?.cls}`}
                          data-testid={`fne-status-${g.groupKey}`}>{FNE_BADGE[g.fneStatus]?.label}</span>
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <span className={`inline-block min-w-[20px] text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          g.anomalies === 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                          {g.anomalies}</span>
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <button onClick={() => openPanelForGroup(g)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:text-blue-900"
                          data-testid={`row-action-${g.groupKey}`}>
                          {g.anomalies > 0 ? <><Pencil className="h-3 w-3" /> Corriger</> : <><Eye className="h-3 w-3" /> Voir</>}
                        </button>
                      </td>
                    </tr>

                    {isOpen && g.contacts.map(c => (
                      <tr key={c.clientId} className="bg-slate-50/60" data-testid={`contact-row-${c.clientId}`}>
                        <td className="px-3 py-1 pl-10 text-slate-700">
                          <div className="truncate max-w-[200px]">{c.contactName}</div>
                          <div className="flex items-center gap-1 text-[10px] text-slate-400">
                            <StatusDot status={c.fields.email.status} />
                            <span className="truncate max-w-[180px]">{c.fields.email.value || 'Email manquant'}</span>
                          </div>
                        </td>
                        <td className="px-3 py-1 text-slate-500 truncate max-w-[130px]">{c.commercialName}</td>
                        <FieldCell
                          field={c.fields.phone} fieldKey="phone"
                          editing={editing?.clientId === c.clientId && editing?.field === 'phone'}
                          editValue={editValue} setEditValue={setEditValue} error={cellError()}
                          onStart={() => startEdit(c.clientId, 'phone', c.fields.phone.value)}
                          onSave={saveCell} onCancel={() => setEditing(null)} />
                        <td colSpan={3} className="px-3 py-1 text-[10px] text-slate-400 italic">
                          NCC, RCCM et adresse sont partagés au niveau de la société
                        </td>
                        <td className="px-3 py-1">
                          <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full border ${FNE_BADGE[c.fneStatus]?.cls}`}>
                            {FNE_BADGE[c.fneStatus]?.label}</span>
                        </td>
                        <td className="px-3 py-1 text-center text-slate-500">{c.anomalies}</td>
                        <td className="px-3 py-1 text-right">
                          <button onClick={() => setPanelId(c.clientId)}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:text-blue-900"
                            data-testid={`contact-action-${c.clientId}`}>
                            <Pencil className="h-3 w-3" /> Corriger
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2 px-3 py-2 border-t border-slate-100 text-[11px] text-slate-500">
          <div data-testid="conformity-count">
            {rows.length === 0 ? '0 société'
              : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, rows.length)} sur ${rows.length} sociétés · ${allContacts.length} comptes`}
          </div>
          <div className="flex items-center gap-2">
            <span>Afficher</span>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
              className="h-7 border border-slate-200 rounded px-1 bg-white" data-testid="page-size">
              {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-2 h-7 border border-slate-200 rounded disabled:opacity-40" data-testid="page-prev">‹</button>
            <span className="tabular-nums">{page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="px-2 h-7 border border-slate-200 rounded disabled:opacity-40" data-testid="page-next">›</button>
          </div>
        </div>
      </div>

      {panelRow && (
        <ClientConformityPanel
          row={panelRow}
          hasNext={panelIndex < allContacts.length - 1}
          onClose={() => setPanelId(null)}
          onSaved={() => { setPanelId(null); fetchTable(); }}
          onSavedAndNext={() => {
            const next = allContacts[panelIndex + 1];
            setPanelId(next ? next.clientId : null);
            fetchTable();
          }}
        />
      )}
    </div>
  );
}
